import * as React from 'react';
import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { AdminGroupsViewCreator } from '@viewCreators/AdminGroupsViewCreator';
import GroupViewModel, { GroupView } from '@views/GroupView';
import GroupSelectedAction from '@actions/Admin/Group/GroupSelectedAction';
import { ActionResponse, CreateGroupAction, GroupChangeAction, UpdateGroupAction } from '@actions';
import { GroupsView } from '@views';
import { GridColDef } from '@mui/x-data-grid';
import { GroupActionCell } from '@components/Admin';
import participantView, { ParticipantView } from '@views/ParticipantView';
import { useNavigate } from 'react-router-dom';
import logger from '@utils/logger';
import { ToLogout } from '@app/navigation/ToLogout';
import { useAppCookies } from '@app/providers/AppCookieProvider';
import { SelectChangeEvent } from '@mui/material';
import { SurveyStore } from '@app/api';
import { CacheStore } from '@app/api/CacheStore';
import { GroupSaveResult, withProblems } from '@actions/Admin/Group/GroupSaveMessage';

export const useGroupAdmin = () => {
  const [groups, setGroups] = useState<{ groups: GroupView[] }>({ groups: [] });
  const [selectedGroup, setSelectedGroup] = useState<GroupView | null>(null);
  const [groupsUpdated, setGroupsUpdated] = useState<boolean>(true);
  const [showEditGroupModal, setShowEditGroupModal] = useState<boolean>(false);
  const [showNewGroupModal, setShowNewGroupModal] = useState<boolean>(false);
  const [showEditParticipantsModal, setShowEditParticipantsModal] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState<number>(0);
  const [saveResult, setSaveResult] = useState<GroupSaveResult | null>(null);
  const surveyStore = new SurveyStore();
  const cacheStore = new CacheStore();

  const cookies = useAppCookies();
  const nav = useNavigate();
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (!hasMounted) return;

    if (!cookies.auth.available) {
      ToLogout();
      return;
    }

    if (groupsUpdated) {
      const viewCreator = new AdminGroupsViewCreator();
      viewCreator.CreateView().then((view: GroupsView) => {
        setGroups(view);
        setGroupsUpdated(false);
      });
    }
  }, [cookies.auth.available, groupsUpdated, hasMounted]);

  const newGroupModalClose = async () => {
    setShowNewGroupModal(false);
  };

  const groupModalClose = async (_e: any, reason?: 'SAVE' | string) => {
    if (!selectedGroup) return;
    setShowEditGroupModal(false);
    const action = new UpdateGroupAction();
    const response = await action.Execute(selectedGroup, reason);
    if (response.status === 'ERROR') {
      console.error(response.error);
    } else {
      resetState();
      setGroupsUpdated(true);
    }
  };

  const groupSelected = async (params: any) => {
    if (params.field === 'ACTIONS') return; // Prevents pagination reset when clicking action cell
    const group = params.row as GroupView;
    const action = new GroupSelectedAction();
    const response = await action.Execute(group, params.field, setSelectedGroup, setShowEditGroupModal, resetState);
    if (response.status === 'ERROR') {
      console.error(response.error);
    }
  };

  const handleCreateGroup = () => {
    resetState();
    setShowNewGroupModal(true);
  };

  const handleClearCache = () => {
    cacheStore.ClearAllCache();
  };

  const handleGroupChange = (e: React.ChangeEvent<HTMLInputElement> | DateTime | SelectChangeEvent) => {
    if (!selectedGroup) return;
    const action = new GroupChangeAction();
    const response = action.Execute(e, selectedGroup, setSelectedGroup);
    if (response.status === 'ERROR') {
      console.error(response.error);
    }
  };

  const handleNewGroupChange = (e: React.ChangeEvent<HTMLInputElement> | DateTime) => {
    if (!selectedGroup) return;
    const action = new GroupChangeAction();
    const response = action.Execute(e, selectedGroup, setSelectedGroup);
    if (response.status === 'ERROR') {
      console.error(response.error);
    }
  };

  const resetState = () => {
    setShowEditGroupModal(false);
    setShowEditParticipantsModal(false);
    setShowNewGroupModal(false);
    setSelectedGroup({ ...GroupViewModel.Empty });
    setActiveStep(0);
  };

  const handleCancel = () => {
    setGroupsUpdated(true);
    resetState();
  };

  const handleAddParticipant = (email: string) => {
    if (!selectedGroup) return;
    // If email is blank or doesn't match the patter *@*.*, return
    if (!email || !/^[^@]+@[^@]+\.[^@]+$/.test(email)) {
      logger.warn('Invalid email format', { email });
      return;
    }

    const emailToUse = email.toLowerCase();

    // Prevent duplicate emails within the same cohort
    const alreadyExists = selectedGroup.participants.some((p) => p.email.toLowerCase() === emailToUse);
    if (alreadyExists) {
      logger.warn('Duplicate email address', { email: emailToUse });
      return;
    }

    selectedGroup.participants.push({
      ...participantView.Empty,
      email: emailToUse,
    });
    setSelectedGroup({ ...selectedGroup });
  };

  const onRemoveParticipant = (e: any) => {
    if (!selectedGroup) return;
    selectedGroup.participants = selectedGroup.participants.filter((x) => x.email !== e.currentTarget.value);
    setSelectedGroup({ ...selectedGroup });
  };

  const handleNextStep = () => {
    setActiveStep(activeStep < 2 ? activeStep + 1 : 0);
  };

  const handlePrevStep = () => {
    setActiveStep(activeStep > 0 ? activeStep - 1 : 0);
  };

  const handleShowAddParticipantDialog = () => {
    setShowEditParticipantsModal(true);
  };

  const handleParticipantModalClose = () => {
    setShowEditParticipantsModal(false);
  };

  const handleSaveResultClose = () => {
    setSaveResult(null);
  };

  // The save may or may not have landed, so drop the editor and show what the server actually has
  const handleSaveResultRefresh = () => {
    setSaveResult(null);
    handleCancel();
  };

  const handleSaveGroup = async (_e: any, reason: string, selectedSurveyIds: number[]) => {
    if (!selectedGroup) return;
    if (reason !== 'SAVE') return;
    const groupName = selectedGroup.name;
    let response: ActionResponse;

    // If selectedGroup has any emails with capital letters, convert them to lowercase
    selectedGroup.participants = selectedGroup.participants.map((p) => {
      return { ...p, email: p.email.toLowerCase() };
    });

    if (selectedGroup.id > 0) {
      const action = new UpdateGroupAction();
      response = await action.Execute(selectedGroup, reason);
    } else {
      const createAction = new CreateGroupAction();
      response = await createAction.Execute(reason, selectedGroup);
      // Assign the new ID to selectedGroup (important for survey assignment after creation)
      if (response.status === 'SUCCESS') {
        const group = response.result?.group as GroupView | undefined;
        selectedGroup.id = group?.id ?? 0;
      }
    }

    let result: GroupSaveResult = response.result.saveResult;

    // On failure the editor stays open underneath the result so the admin's edits aren't lost
    if (response.status === 'ERROR') {
      logger.error('Team save failed', { groupName, outcome: result.outcome, message: result.message });
      setSaveResult(result);
      return;
    }

    if (selectedGroup.id > 0) {
      try {
        await surveyStore.syncCohortSurveys(selectedGroup.id, selectedSurveyIds);
      } catch (e) {
        logger.error('Team saved but survey assignment failed', { groupName, error: e });
        result = withProblems(result, [
          'Surveys were not assigned to the team. Open the team and save again to retry.',
        ]);
      }
    }
    setGroupsUpdated(true);
    resetState();
    setSaveResult(result);
  };

  const handleRoleChange = (e: any, participant: ParticipantView) => {
    if (!selectedGroup) return;
    const newRole = String(e.currentTarget.value);
    const participantIdx = selectedGroup.participants.findIndex((x) => x.email === participant.email);

    if (e.currentTarget.checked) {
      if (participant.role === '') {
        participant.role = newRole;
        setSelectedGroup((g) => {
          if (!g) return null;
          g.participants[participantIdx] = { ...participant };
          return { ...g };
        });
        return;
      }
      const roles = participant.role.split('|').filter(Boolean);
      roles.push(newRole);
      if (roles.length === 1) participant.role = roles[0];
      else participant.role = roles.join('|');
    } else {
      const roles = participant.role.split('|').filter(Boolean);

      let roleIndex = roles.indexOf(newRole);
      while (roleIndex > -1) {
        roles.splice(roleIndex, 1);
        roleIndex = roles.indexOf(newRole);
      }

      participant.role = roles.join('|');
    }
    setSelectedGroup((g) => {
      if (!g) return null;
      g.participants[participantIdx] = { ...participant };
      return { ...g };
    });
  };

  const [riskReportOpen, setRiskReportOpen] = useState(false);

  const handleRiskReport = () => {
    setRiskReportOpen(true);
  };

  const handleRiskReportClose = () => {
    setRiskReportOpen(false);
  };

  const cols = [
    {
      type: 'string',
      headerName: 'id',
      editable: false,
      field: 'id',
      disableExport: true,
    },
    {
      type: 'string',
      headerName: 'Pledge',
      valueFormatter: (value) => {
        return Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(value);
      },
      editable: false,
      field: 'pledge',
      disableExport: true,
    },
    {
      type: 'string',
      headerName: 'Name',
      flex: 1,
      editable: false,
      field: 'name',
      disableExport: true,
      minWidth: 200,
    },
    {
      type: 'date',
      headerName: 'Start Date',
      editable: false,
      field: 'startDate',
      disableExport: true,
      minWidth: 200,
      valueGetter: (value: string | DateTime) => {
        if (!value) return null;
        if (typeof value === 'string') {
          return DateTime.fromISO(value).toJSDate();
        }
        if (DateTime.isDateTime(value)) {
          return value.toJSDate();
        }
        return null;
      },
    },
    {
      type: 'date',
      headerName: 'End Date',
      editable: false,
      field: 'endDate',
      disableExport: true,
      minWidth: 200,
      valueGetter: (value: string | null) => {
        if (!value) return null;
        return DateTime.fromISO(value).toJSDate();
      },
    },
    {
      type: 'string',
      headerName: 'Status',
      editable: false,
      field: 'finalized',
      disableExport: true,
      minWidth: 120,
      valueGetter: (value: boolean) => {
        return value ? 'Closed' : 'Open';
      },
    },
    {
      field: 'ACTIONS',
      headerName: 'Actions',
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return <GroupActionCell group={params.row} updateSummary={() => setGroupsUpdated(true)}></GroupActionCell>;
      },
    },
  ] as GridColDef[];

  return {
    groups,
    selectedGroup,
    showEditGroupModal,
    showNewGroupModal,
    showEditParticipantsModal,
    activeStep,
    handleCreateGroup,
    groupModalClose,
    newGroupModalClose,
    groupSelected,
    handleGroupChange,
    handleNewGroupChange,
    handleCancel,
    handleAddParticipant,
    onRemoveParticipant,
    handleNextStep,
    handlePrevStep,
    handleShowAddParticipantDialog,
    handleParticipantModalClose,
    handleSaveGroup,
    handleRoleChange,
    handleClearCache,
    handleRiskReport,
    handleRiskReportClose,
    riskReportOpen,
    saveResult,
    handleSaveResultClose,
    handleSaveResultRefresh,
    cols,
  };
};
